import { Component, effect, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DokaModalController,
  DokaModal,
  DokaSelect,
  DokaSelectOption,
  DokaNumberInput,
  DokaTextField,
  DokaButton,
} from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import {
  AvailabilityState,
  CatalogClient,
  CatalogItemTemplateModel,
  CreateOrUpdateCatalogItemTemplateModel,
  CurrenciesClient,
  GetCountriesResponse,
  ICreateOrUpdateCatalogItemTemplateModel,
  LocationsClient,
  MinimalCatalogItemModel,
} from 'src/api/services/generated/api-clients';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { CreateOrUpdateTemplateFormType } from './create-or-update-template.form.type';
import { firstValueFrom, of } from 'rxjs';

@Component({
  templateUrl: 'create-or-update-template-slide-over.component.html',
  imports: [
    DokaModal,
    I18NextEagerPipe,
    DokaSelect,
    DokaSelectOption,
    DokaNumberInput,
    DokaTextField,
    ReactiveFormsModule,
    DokaButton,
  ],
})
export class CreateOrUpdateTemplateSlideOverComponent {
  protected AvailabilityState = AvailabilityState;

  private readonly catalogClient = inject(CatalogClient);
  private readonly locationsClient = inject(LocationsClient);
  private readonly currenciesClient = inject(CurrenciesClient);
  private readonly modalCtrl = inject(DokaModalController);
  protected readonly userInfoService = inject(UserInfoService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(ToastService);
  private readonly i18n = inject(I18NextService);

  protected isLoading = signal(false);
  public readonly catalogItem = signal<MinimalCatalogItemModel | undefined>(undefined);
  public readonly itemTemplate = signal<CatalogItemTemplateModel | undefined>(undefined);
  protected availablityStateKeys = Object.values(AvailabilityState);

  protected form: FormGroup<CreateOrUpdateTemplateFormType> = this.fb.group({
    countryId: this.fb.control(this.itemTemplate()?.countryId ?? '', [Validators.required]),
    isoCurrencyCode: this.fb.control(this.itemTemplate()?.isoCurrencyCode ?? '', [Validators.required]),
    cost: this.fb.control(this.itemTemplate()?.cost ?? 0, [Validators.required, Validators.min(1)]),
    lifetimeInMonths: this.fb.control<number | null>(this.itemTemplate()?.lifetimeInMonths ?? null, [Validators.min(1)]),
    deliveryTimeInMonths: this.fb.control(this.itemTemplate()?.estimatedDeliveryTime ?? '', [Validators.required]),
    availabilityState: this.fb.control(this.itemTemplate()?.availabilityState ?? null, [Validators.required]),
  });

  protected readonly countries = rxResource({
    params: () => this.catalogItem()?.divisionId,
    stream: ({ params: divisionId }) => {
      if (!divisionId) {
        const response = new GetCountriesResponse();
        response.data = [];
        return of(response);
      }

      return this.locationsClient.getCountries(divisionId);
    },
  });

  protected readonly currencies = rxResource({
    stream: () => this.currenciesClient.getCurrencies(),
  });

  protected getSymbolForCurrencyCode(isoCurrencyCode: string | null | undefined): string {
    const currency = this.currencies.value()?.data?.find((c) => c.isoCurrencyCode === isoCurrencyCode);
    return currency ? currency.currencySymbol : isoCurrencyCode || '';
  }

  protected closeModal(result?: boolean, role?: string) {
    this.modalCtrl.dismiss(result, role);
  }

  protected itemTemplateHasValue = effect(() => {
    this.form = this.fb.group({
      countryId: this.fb.control(this.itemTemplate()?.countryId ?? '', [Validators.required]),
      isoCurrencyCode: this.fb.control(this.itemTemplate()?.isoCurrencyCode ?? '', [Validators.required]),
      cost: this.fb.control(this.itemTemplate()?.cost ?? 0, [Validators.required, Validators.min(1)]),
      lifetimeInMonths: this.fb.control<number | null>(this.itemTemplate()?.lifetimeInMonths ?? null, [Validators.min(1)]),
      deliveryTimeInMonths: this.fb.control(this.itemTemplate()?.estimatedDeliveryTime ?? '', [Validators.required]),
      availabilityState: this.fb.control(this.itemTemplate()?.availabilityState ?? null, [Validators.required]),
    });
  });

  protected async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    const value = this.form.getRawValue() as ICreateOrUpdateCatalogItemTemplateModel;
    const catalogItem = this.catalogItem();

    if (!catalogItem) {
      throw new Error('Catalog item is required to create a template.');
    }

    try {
      if (this.itemTemplate()?.id) {
        await firstValueFrom(
          this.catalogClient.updateCatalogItemTemplate(
            this.itemTemplate()!.id!,
            new CreateOrUpdateCatalogItemTemplateModel(value),
          ),
        );
      } else {
        await firstValueFrom(
          this.catalogClient.createCatalogItemTemplate(
            catalogItem.id,
            new CreateOrUpdateCatalogItemTemplateModel(value),
          ),
        );
      }

      this.closeModal(true, 'created');
    } catch {
      await this.toastService.showError(this.i18n.t('MasterData.TemplateSlideOver.SaveError'));
    } finally {
      this.isLoading.set(false);
    }
  }
}
